import { Layer } from '@deck.gl/core/typed';
import { MapContext } from 'components/maps/MapContext';
import { MapLayerType, useGetMapLayerConfigsQuery } from '@kinetic-ui/data-layer';
import { StyleSpecification } from 'maplibre-gl';
import { useContext, useEffect, useMemo, useState } from 'react';
import { GoogleMapType, useDeckGlTileLayersResult } from './types';
import { useTranslation } from 'react-i18next';
import {
  getGoogleLayerUrl,
  mapConfigToStyle,
  mapConfigToLayer,
  getMapLibreVectorLayer,
} from './utils';

interface GoogleSession {
  session: string;
  expiry: number;
}

export const GOOGLE_SESSION_MAP = new Map<string, GoogleSession>();

const DEFAULT_MAP_BASE_LAYER = 'DEFAULT_MAP_BASE_LAYER';

export type ColorData = { propertyColor: string; propertyValue: number[]; propertyName?: string };

export const useDeckGlTileLayers = (
  isLayerPreview: boolean,
  currentZoom: number | undefined
): useDeckGlTileLayersResult => {
  const { t } = useTranslation('maps');
  const layerResult = useGetMapLayerConfigsQuery();
  const [layers, setLayers] = useState<Layer[]>([]);
  const { selectedLayers, registerLayer } = useContext(MapContext);
  const [googleLayerUrl, setGoogleLayerUrl] = useState<string>();

  useEffect(() => {
    // Registering a layer adds it to the Layers Menu
    layerResult.data?.mapLayerConfigs.forEach((layer) => {
      if (
        (isLayerPreview && !layer.baseLayer) ||
        ((!isLayerPreview || layer.baseLayer) && !layer.visible)
      ) {
        return;
      }
      registerLayer({
        group: layer.group ?? t('overlay'),
        id: layer.id,
        label: layer.name,
        type: layer.baseLayer ? 'radio' : 'checkbox',
        baseLayer: layer.baseLayer,
      });
    });
  }, [isLayerPreview, layerResult.data?.mapLayerConfigs, registerLayer, t]);

  // only load one base layer. if no base layers are selected use default.
  const selectedBaseLayer = layerResult.data?.mapLayerConfigs
    ?.filter((config) => config.baseLayer)
    .find((config) => selectedLayers?.includes(config.id));
  const selectedBaseLayerId = selectedBaseLayer?.visible
    ? selectedBaseLayer.id
    : DEFAULT_MAP_BASE_LAYER;

  const selectedBaseLayerConfig = useMemo(() => {
    return layerResult.data?.mapLayerConfigs?.find((config) => config.id === selectedBaseLayerId);
  }, [layerResult.data?.mapLayerConfigs, selectedBaseLayerId]);

  // get the google layer session
  useEffect(() => {
    const layer = layerResult.data?.mapLayerConfigs?.find(
      (config) => config.id === selectedBaseLayerId
    );
    if (!layer || layer.type !== MapLayerType.Google) {
      return;
    }
    const region = layer.data.googleMapRegion ? layer.data.googleMapRegion : 'US';
    const language = layer.data.googleMapLanguage ? layer.data.googleMapLanguage : 'en-US';
    getGoogleLayerUrl(
      layer.data.googleApiKey,
      layer.data.googleMapType as GoogleMapType,
      region,
      language
    ).then((url) => setGoogleLayerUrl(url));
    const interval = setInterval(async () => {
      setGoogleLayerUrl(
        await getGoogleLayerUrl(
          layer.data.googleApiKey,
          layer.data.googleMapType as GoogleMapType,
          region,
          language
        )
      );
    }, 60_000);
    return () => {
      clearInterval(interval);
      setGoogleLayerUrl(undefined);
    };
  }, [layerResult.data?.mapLayerConfigs, selectedBaseLayerId]);

  const style: StyleSpecification | undefined = useMemo(() => {
    return mapConfigToStyle(
      layerResult.data?.mapLayerConfigs?.find((config) => config.id === selectedBaseLayerId)!,
      googleLayerUrl
    );
  }, [googleLayerUrl, layerResult.data?.mapLayerConfigs, selectedBaseLayerId]);

  useEffect(() => {
    const promises: NodeJS.Timeout[] = [];

    async function loadLayers() {
      const loadedLayers: Layer[] = [];
      for (const layer of layerResult.data?.mapLayerConfigs ?? []) {
        if (
          layer.baseLayer ||
          layer.type === MapLayerType.Vector ||
          layer.type === MapLayerType.Google // only supported on base layer
        ) {
          continue;
        }

        const zoom = currentZoom ?? 0;
        const minZoom = parseInt(layer.data.minZoom);
        const maxZoom = parseInt(layer.data.maxZoom);

        const visible =
          selectedLayers.includes(layer.id) && zoom > minZoom && zoom < maxZoom && layer.visible;
        const deckGlLayer = visible ? await mapConfigToLayer(layer, currentZoom) : null;
        if (deckGlLayer) {
          loadedLayers.push(deckGlLayer);
          let refreshRate = layer.data.refreshRate ? layer.data.refreshRate * 1000 : 0;

          if (refreshRate && layer.data.baseUrl) {
            promises.push(
              setInterval(async () => {
                const newLayers = [...loadedLayers];
                const layerIndex = newLayers.findIndex((l) => l.id === layer.id);
                newLayers.splice(layerIndex, 1);
                let newUrl = `${layer.data.baseUrl}&v=${Date.now()}`;
                if (layer.data.baseUrl.indexOf('?') === -1) {
                  newUrl = `${layer.data.baseUrl}?v=${Date.now()}`;
                }
                const newLayer = {
                  ...layer,
                  data: { ...layer.data, baseUrl: newUrl },
                };
                newLayers.push((await mapConfigToLayer(newLayer, currentZoom ?? 0))!);
                setLayers(newLayers);
              }, refreshRate)
            );
          }
        }
      }

      setLayers(loadedLayers);
    }
    loadLayers();
    return () => {
      promises.forEach((interval) => clearInterval(interval));
    };
  }, [selectedLayers, layerResult.data?.mapLayerConfigs, currentZoom]);

  // Includes icon cluster layers
  const mapLayers = useMemo(() => {
    return (
      <>
        {layerResult.data?.mapLayerConfigs
          ?.filter(
            (config) => config.type === MapLayerType.Vector && selectedLayers.includes(config.id)
          )
          .map((mapConfig) => {
            return getMapLibreVectorLayer(mapConfig);
          }) ?? []}
      </>
    );
  }, [layerResult.data?.mapLayerConfigs, selectedLayers]);

  return {
    style,
    layers,
    mapLayers,
    selectedBaseLayerConfig,
  };
};
